import { useState, useEffect, useCallback } from 'react';
import { hotelApi } from '../../hotelApi';

const EMPTY = {
  hotelName: '', address: '', phone: '', email: '', gstNo: '',
  taxPct: '5', billHeader: '', billFooter: '',
};

const DEFAULT_POS = {
  paperWidth: '80',
  fontFamily: 'Courier New',
  headerSize: '16',
  bodySize: '13',
  footerSize: '11',
  headerAlign: 'center',
  divider: 'dashed',
  boldHotelName: true,
  boldTotals: true,
  showAddress: true,
  showPhone: true,
  showEmail: false,
  showGst: true,
  showTableNo: true,
  showOrderNo: true,
  showDate: true,
};

const inp = { className: 'form-control' };

function F({ label, children, hint }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: '#888', marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label onClick={onChange}
      style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
        padding: '8px 12px', borderRadius: 8, userSelect: 'none',
        background: checked ? '#e6f4ea' : '#f8f9fa',
        border: `1px solid ${checked ? '#34a853' : '#e8eaed'}`, transition: 'all .15s' }}>
      <div style={{ width: 36, height: 20, background: checked ? '#137333' : '#ccc',
        borderRadius: 10, position: 'relative', transition: 'background .15s', flexShrink: 0 }}>
        <div style={{ width: 16, height: 16, background: '#fff', borderRadius: '50%',
          position: 'absolute', top: 2, left: checked ? 18 : 2, transition: 'left .15s' }} />
      </div>
      <span style={{ fontSize: 13, fontWeight: 500, color: checked ? '#0d3321' : '#5f6368' }}>
        {label}
      </span>
    </label>
  );
}

function SegBtn({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {options.map(([v, label]) => (
        <button key={v} onClick={() => onChange(v)}
          style={{ flex: 1, padding: '7px 4px', borderRadius: 8, border: '2px solid', cursor: 'pointer',
            borderColor: value === v ? '#137333' : '#e8eaed',
            background: value === v ? '#e6f4ea' : '#fff',
            color: value === v ? '#0d3321' : '#5f6368',
            fontWeight: value === v ? 700 : 500, fontSize: 12 }}>
          {label}
        </button>
      ))}
    </div>
  );
}

export default function HotelSetup() {
  const [tab, setTab]       = useState('info');
  const [form, setForm]     = useState(EMPTY);
  const [pos, setPos]       = useState(DEFAULT_POS);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [err, setErr]       = useState('');

  const load = useCallback(() => {
    hotelApi.getInfo().then(d => {
      if (d && Object.keys(d).length > 0) {
        setForm({
          hotelName:  d.hotel_name  || '',
          address:    d.address     || '',
          phone:      d.phone       || '',
          email:      d.email       || '',
          gstNo:      d.gst_no      || '',
          taxPct:     d.tax_pct != null ? String(d.tax_pct) : '5',
          billHeader: d.bill_header || '',
          billFooter: d.bill_footer || '',
        });
        if (d.pos_config) {
          try { setPos({ ...DEFAULT_POS, ...JSON.parse(d.pos_config) }); } catch (_) {}
        }
      }
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true); setErr(''); setSaved(false);
    try {
      await hotelApi.upsertInfo({
        ...form,
        taxPct: parseFloat(form.taxPct) || 0,
        posConfig: JSON.stringify(pos),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const sp = (key) => (val) => setPos(prev => ({ ...prev, [key]: val }));
  const tp = (key) => () => setPos(prev => ({ ...prev, [key]: !prev[key] }));

  // preview computed values
  const hSize = parseInt(pos.headerSize) || 16;
  const bSize = parseInt(pos.bodySize)   || 13;
  const fSize = parseInt(pos.footerSize) || 11;
  const scale = 0.82;
  const divBorder = pos.divider === 'dashed' ? '1px dashed #999'
                  : pos.divider === 'solid'  ? '1px solid #000' : 'none';
  const Divider = () => pos.divider !== 'none'
    ? <div style={{ borderTop: divBorder, margin: '6px 0' }} />
    : <div style={{ margin: '4px 0' }} />;

  const taxAmt   = (400 * (parseFloat(form.taxPct) || 5) / 100);
  const preTotal = (400 + taxAmt).toFixed(2);

  return (
    <div>
      <div className="tabs" style={{ marginBottom: 16 }}>
        {[
          { key: 'info', label: '🏨 Hotel Info' },
          { key: 'bill', label: '🧾 Bill Settings' },
          { key: 'pos',  label: '🖨️ POS Printer' },
        ].map(t => (
          <button key={t.key} className={`tab${tab === t.key ? ' active' : ''}`}
            onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">
            {tab === 'info' ? 'Hotel Information'
              : tab === 'bill' ? 'Bill & Tax Settings'
              : 'POS Printer Layout & Fonts'}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {saved && <span style={{ color: '#137333', fontSize: 13, fontWeight: 600 }}>✓ Saved</span>}
            <button className="btn btn-primary" style={{ fontSize: 13, padding: '6px 18px' }}
              onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : '💾 Save Settings'}
            </button>
          </div>
        </div>

        <div className="card-body">
          {err && (
            <div style={{ color: '#c5221f', background: '#fce8e6', border: '1px solid #f5c6c6',
              borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>⚠️ {err}</div>
          )}

          {/* ── Hotel Info ── */}
          {tab === 'info' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
              <F label="Hotel Name *">
                <input {...inp} value={form.hotelName} placeholder="e.g. The Grand Palace"
                  onChange={e => setForm(f => ({ ...f, hotelName: e.target.value }))} />
              </F>
              <F label="Phone">
                <input {...inp} value={form.phone} placeholder="+91 98765 43210"
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
              </F>
              <F label="Email">
                <input {...inp} type="email" value={form.email} placeholder="info@hotel.com"
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </F>
              <F label="GST / Tax Reg. No">
                <input {...inp} value={form.gstNo} placeholder="27AAAAA0000A1Z5"
                  onChange={e => setForm(f => ({ ...f, gstNo: e.target.value }))} />
              </F>
              <F label="Address">
                <textarea {...inp} rows={3} value={form.address}
                  placeholder="Full address including city, state, PIN"
                  onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                  style={{ resize: 'vertical' }} />
              </F>
            </div>
          )}

          {/* ── Bill Settings ── */}
          {tab === 'bill' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
              <F label="Tax Percentage (%)"
                hint="Applied to every bill automatically. Can be overridden per bill.">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input {...inp} type="number" min={0} max={100} step={0.5}
                    style={{ maxWidth: 120 }} value={form.taxPct}
                    onChange={e => setForm(f => ({ ...f, taxPct: e.target.value }))} />
                  <span style={{ fontSize: 14, color: '#5f6368' }}>%</span>
                  <span style={{ background: '#e6f4ea', color: '#137333', borderRadius: 8,
                    padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
                    On ₹100 → Tax ₹{(parseFloat(form.taxPct) || 0).toFixed(2)}
                  </span>
                </div>
              </F>
              <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <F label="Bill Header Text" hint="Printed at the top of every bill.">
                  <textarea {...inp} rows={3} value={form.billHeader}
                    placeholder={'e.g.\nWelcome to The Grand Palace\nAuthentic Indian Cuisine'}
                    onChange={e => setForm(f => ({ ...f, billHeader: e.target.value }))}
                    style={{ resize: 'vertical', fontFamily: 'inherit' }} />
                </F>
                <F label="Bill Footer Text" hint="Printed at the bottom of every bill.">
                  <textarea {...inp} rows={3} value={form.billFooter}
                    placeholder={'e.g.\nThank you for dining with us!\nVisit us again soon.'}
                    onChange={e => setForm(f => ({ ...f, billFooter: e.target.value }))}
                    style={{ resize: 'vertical', fontFamily: 'inherit' }} />
                </F>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0d3321', marginBottom: 8 }}>🧾 Bill Preview</div>
                <div style={{ background: '#fff', border: '1px dashed #ccc', borderRadius: 8,
                  padding: '16px', fontFamily: 'monospace', fontSize: 12.5, maxWidth: 320,
                  lineHeight: 1.7, color: '#202124' }}>
                  <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 14 }}>{form.hotelName || 'Hotel Name'}</div>
                  {form.address && <div style={{ textAlign: 'center', fontSize: 11, color: '#555' }}>{form.address}</div>}
                  {form.phone   && <div style={{ textAlign: 'center', fontSize: 11, color: '#555' }}>📞 {form.phone}</div>}
                  {form.gstNo   && <div style={{ textAlign: 'center', fontSize: 11, color: '#555' }}>GST: {form.gstNo}</div>}
                  {form.billHeader && <><div style={{ borderTop: '1px dashed #ccc', margin: '8px 0' }} />
                    <div style={{ textAlign: 'center', color: '#137333', fontSize: 12 }}>{form.billHeader}</div></>}
                  <div style={{ borderTop: '1px dashed #ccc', margin: '8px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#888' }}>
                    <span>Bill No: BILL-XXXX</span><span>Table: T1</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#888' }}>Date: {new Date().toLocaleDateString('en-IN')}</div>
                  <div style={{ borderTop: '1px dashed #ccc', margin: '8px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Sample Item × 2</span><span>₹100</span></div>
                  <div style={{ borderTop: '1px dashed #ccc', margin: '8px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Subtotal</span><span>₹100</span></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Tax ({form.taxPct || 0}%)</span>
                    <span>₹{((100 * parseFloat(form.taxPct || 0)) / 100).toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800 }}>
                    <span>TOTAL</span><span>₹{(100 + (100 * parseFloat(form.taxPct || 0)) / 100).toFixed(2)}</span>
                  </div>
                  {form.billFooter && <><div style={{ borderTop: '1px dashed #ccc', margin: '8px 0' }} />
                    <div style={{ textAlign: 'center', fontSize: 11, color: '#555' }}>{form.billFooter}</div></>}
                </div>
              </div>
            </div>
          )}

          {/* ── POS Printer ── */}
          {tab === 'pos' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 28, alignItems: 'start' }}>

              {/* Left: settings */}
              <div>

                {/* Paper & Font */}
                <div style={{ marginBottom: 22 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#5f6368', textTransform: 'uppercase',
                    letterSpacing: 1, marginBottom: 12, paddingBottom: 6, borderBottom: '2px solid #e6f4ea' }}>
                    📄 Paper & Font
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                    <F label="Paper Width">
                      <SegBtn options={[['58','58 mm'],['80','80 mm']]}
                        value={pos.paperWidth} onChange={sp('paperWidth')} />
                    </F>
                    <F label="Font Family">
                      <select {...inp} value={pos.fontFamily} onChange={e => sp('fontFamily')(e.target.value)}>
                        <option value="Courier New">Courier New (Thermal)</option>
                        <option value="monospace">Monospace (System)</option>
                        <option value="Arial">Arial (Sans-serif)</option>
                        <option value="Tahoma">Tahoma</option>
                        <option value="Times New Roman">Times New Roman</option>
                        <option value="Verdana">Verdana</option>
                      </select>
                    </F>
                    <F label="Header Font Size">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input {...inp} type="number" min={10} max={28} style={{ maxWidth: 72 }}
                          value={pos.headerSize}
                          onChange={e => sp('headerSize')(e.target.value)} />
                        <span style={{ fontSize: 11, color: '#888' }}>px</span>
                        <span style={{ fontFamily: `'${pos.fontFamily}',monospace`,
                          fontSize: hSize * 0.65, fontWeight: 800, color: '#137333' }}>
                          {form.hotelName || 'Hotel'}
                        </span>
                      </div>
                    </F>
                    <F label="Body Font Size">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input {...inp} type="number" min={8} max={20} style={{ maxWidth: 72 }}
                          value={pos.bodySize}
                          onChange={e => sp('bodySize')(e.target.value)} />
                        <span style={{ fontSize: 11, color: '#888' }}>px</span>
                        <span style={{ fontFamily: `'${pos.fontFamily}',monospace`,
                          fontSize: bSize * 0.65, color: '#5f6368' }}>
                          Paneer Masala ₹280
                        </span>
                      </div>
                    </F>
                    <F label="Footer Font Size">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input {...inp} type="number" min={8} max={16} style={{ maxWidth: 72 }}
                          value={pos.footerSize}
                          onChange={e => sp('footerSize')(e.target.value)} />
                        <span style={{ fontSize: 11, color: '#888' }}>px</span>
                        <span style={{ fontFamily: `'${pos.fontFamily}',monospace`,
                          fontSize: fSize * 0.65, color: '#888' }}>
                          Thank you!
                        </span>
                      </div>
                    </F>
                    <F label="Header Alignment">
                      <SegBtn options={[['left','⬅ Left'],['center','↔ Center'],['right','Right ➡']]}
                        value={pos.headerAlign} onChange={sp('headerAlign')} />
                    </F>
                    <F label="Divider Style">
                      <SegBtn options={[['dashed','- - -'],['solid','───'],['none','None']]}
                        value={pos.divider} onChange={sp('divider')} />
                    </F>
                  </div>
                </div>

                {/* Style options */}
                <div style={{ marginBottom: 22 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#5f6368', textTransform: 'uppercase',
                    letterSpacing: 1, marginBottom: 12, paddingBottom: 6, borderBottom: '2px solid #e6f4ea' }}>
                    ✏️ Style Options
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                    <Toggle label="Bold Hotel Name" checked={pos.boldHotelName} onChange={tp('boldHotelName')} />
                    <Toggle label="Bold Totals"     checked={pos.boldTotals}    onChange={tp('boldTotals')} />
                  </div>
                </div>

                {/* Receipt content */}
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#5f6368', textTransform: 'uppercase',
                    letterSpacing: 1, marginBottom: 12, paddingBottom: 6, borderBottom: '2px solid #e6f4ea' }}>
                    📋 Receipt Content
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                    <Toggle label="Show Address"   checked={pos.showAddress}  onChange={tp('showAddress')} />
                    <Toggle label="Show Phone"     checked={pos.showPhone}    onChange={tp('showPhone')} />
                    <Toggle label="Show Email"     checked={pos.showEmail}    onChange={tp('showEmail')} />
                    <Toggle label="Show GST No"    checked={pos.showGst}      onChange={tp('showGst')} />
                    <Toggle label="Show Table No"  checked={pos.showTableNo}  onChange={tp('showTableNo')} />
                    <Toggle label="Show Order No"  checked={pos.showOrderNo}  onChange={tp('showOrderNo')} />
                    <Toggle label="Show Date/Time" checked={pos.showDate}     onChange={tp('showDate')} />
                  </div>
                </div>
              </div>

              {/* Right: live receipt preview */}
              <div style={{ minWidth: pos.paperWidth === '58' ? 200 : 260 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0d3321', marginBottom: 10 }}>
                  🖨️ Live Preview
                </div>
                <div style={{
                  background: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  padding: '14px 10px',
                  width: pos.paperWidth === '58' ? 200 : 260,
                  fontFamily: `'${pos.fontFamily}', monospace`,
                  fontSize: bSize * scale,
                  lineHeight: 1.65,
                  color: '#111',
                  boxShadow: '2px 4px 16px rgba(0,0,0,.12)',
                }}>
                  <div style={{ textAlign: pos.headerAlign,
                    fontWeight: pos.boldHotelName ? 800 : 600,
                    fontSize: hSize * scale }}>
                    {form.hotelName || 'Your Hotel'}
                  </div>
                  {pos.showAddress && form.address && (
                    <div style={{ textAlign: pos.headerAlign, fontSize: fSize * scale, color: '#555' }}>
                      {form.address}
                    </div>
                  )}
                  {pos.showPhone && form.phone && (
                    <div style={{ textAlign: pos.headerAlign, fontSize: fSize * scale }}>
                      ☎ {form.phone}
                    </div>
                  )}
                  {pos.showEmail && form.email && (
                    <div style={{ textAlign: pos.headerAlign, fontSize: fSize * scale }}>
                      ✉ {form.email}
                    </div>
                  )}
                  {pos.showGst && form.gstNo && (
                    <div style={{ textAlign: pos.headerAlign, fontSize: fSize * scale }}>
                      GST: {form.gstNo}
                    </div>
                  )}
                  {form.billHeader && (
                    <>
                      <Divider />
                      <div style={{ textAlign: 'center', fontSize: fSize * scale,
                        color: '#1a5c31', whiteSpace: 'pre-line' }}>
                        {form.billHeader}
                      </div>
                    </>
                  )}
                  <Divider />
                  <div style={{ fontSize: fSize * scale, color: '#555' }}>
                    {pos.showOrderNo && <span>Bill: BILL-0001</span>}
                    {pos.showTableNo && <span style={{ marginLeft: 8 }}>Table: T1</span>}
                  </div>
                  {pos.showDate && (
                    <div style={{ fontSize: fSize * scale, color: '#555' }}>
                      {new Date().toLocaleDateString('en-IN')}{' '}
                      {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                  <Divider />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Paneer Masala x2</span><span>₹280</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Butter Naan x4</span><span>₹120</span>
                  </div>
                  <Divider />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: fSize * scale }}>
                    <span>Subtotal</span><span>₹400</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: fSize * scale }}>
                    <span>Tax ({form.taxPct || 5}%)</span>
                    <span>₹{taxAmt.toFixed(2)}</span>
                  </div>
                  <div style={{ borderTop: '2px solid #111', marginTop: 4, paddingTop: 4,
                    display: 'flex', justifyContent: 'space-between',
                    fontWeight: pos.boldTotals ? 800 : 600,
                    fontSize: bSize * scale * 1.05 }}>
                    <span>TOTAL</span><span>₹{preTotal}</span>
                  </div>
                  {form.billFooter && (
                    <>
                      <Divider />
                      <div style={{ textAlign: 'center', fontSize: fSize * scale,
                        color: '#555', whiteSpace: 'pre-line' }}>
                        {form.billFooter}
                      </div>
                    </>
                  )}
                  <div style={{ textAlign: 'center', fontSize: 9, color: '#aaa', marginTop: 6 }}>
                    Powered by SmaBizZ
                  </div>
                </div>
                <div style={{ marginTop: 8, fontSize: 11, color: '#888', textAlign: 'center' }}>
                  {pos.paperWidth}mm · {pos.fontFamily}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
